import { parseDecimal } from 'app/modules/utilities/shared/decimal-input';

export type TurnDirection = 'left' | 'right';

/** Decimals kept for widths and spacings, matching the terminal input precision. */
export const WIDTH_DIGITS = 2;

/** Decimals kept for the lateral offset, matching the terminal input precision. */
export const OFFSET_DIGITS = 3;

/**
 * Decimals the scaled value is fixed to before rounding. Inputs carry at most
 * 3 decimals, so any value within 1e-6 of a half is a half in decimal terms;
 * the gap is only binary error from representation (2.385) or from the
 * subtractions in the formulas (3.05 + 3.025, 4.065 − 4.04).
 */
const ROUNDING_TOLERANCE_DECIMALS = 6;

/** Adjustment passes are numbered from 1. */
export const FIRST_PASS_NUMBER = 1;

/**
 * Separator written back into the inputs when the caller does not pass the
 * user's one. Parsing accepts both separators, so drafts saved with either
 * one keep working.
 */
export const DEFAULT_DECIMAL_SEPARATOR = ',';

/**
 * Record of a completed adjustment pass. Keeps both the inputs used and the
 * result, so the history can be displayed and the user can go back to the
 * previous pass without losing what was measured in the field.
 */
export interface PassSnapshot {
  passNumber: number;
  implementWidth: number;
  lateralOffset: number;
  turnDirection: TurnDirection;
  measuredSpacing12: string;
  measuredSpacing23: string;
  correctedWidth: number;
  correctedOffset: number;
}

export interface PassToPassDraft {
  /**
   * Current adjustment pass, starting at 1. Not to be confused with the
   * 1st/2nd/3rd tracks of the measurement fields, which are the three tracks
   * driven inside each adjustment pass.
   */
  passNumber: number;
  lineSpacing: string;
  lineCount: string;
  lateralOffset: string;
  turnDirection: TurnDirection;
  measuredSpacing12: string;
  measuredSpacing23: string;
  implementWidthOverride: number | null;
  history: PassSnapshot[];
}

export type DraftInputField = keyof Pick<
  PassToPassDraft,
  | 'lineSpacing'
  | 'lineCount'
  | 'lateralOffset'
  | 'measuredSpacing12'
  | 'measuredSpacing23'
>;

export type ValidationErrorCode =
  | 'lineSpacingRequired'
  | 'lineSpacingNegative'
  | 'lineCountRequired'
  | 'lineCountNegative'
  | 'measuredSpacingRequired'
  | 'measuredSpacingNegative'
  | 'lateralOffsetRequired'
  | 'implementWidthNotPositive'
  | 'correctedWidthNotPositive';

export interface PassToPassCalculation {
  isValid: boolean;
  errors: ValidationErrorCode[];
  implementWidth: number | null;
  correctedWidth: number | null;
  correctedOffset: number | null;
}

export const createEmptyPassToPassDraft = (): PassToPassDraft => ({
  passNumber: FIRST_PASS_NUMBER,
  lineSpacing: '',
  lineCount: '',
  lateralOffset: '',
  turnDirection: 'left',
  measuredSpacing12: '',
  measuredSpacing23: '',
  implementWidthOverride: null,
  history: [],
});

/**
 * Rounds half away from zero, like the spreadsheet the formulas come from,
 * instead of JavaScript's round-half-up (`Math.round(-0.5) === -0`).
 *
 * @remarks
 * The scaled value is first fixed to a few decimals so a half that is only a
 * half in decimal (2.385 is 2.38499999… in binary) still rounds up, as it does
 * in the spreadsheet. Without it the result can differ by one unit in the last
 * digit, which is exactly the value the operator types into the terminal.
 */
export const roundLikeExcel = (value: number, digits: number): number => {
  const factor = 10 ** digits;
  const sign = Math.sign(value) || 1;
  const scaled = Number(
    (Math.abs(value) * factor).toFixed(ROUNDING_TOLERANCE_DECIMALS),
  );
  const rounded = (sign * Math.round(scaled)) / factor;
  if (Object.is(rounded, -0)) {
    return 0;
  }
  return rounded;
};

/** Formats a value for the input fields, without trailing zeros. */
export const formatDecimal = (
  value: number,
  digits: number,
  decimalSeparator = DEFAULT_DECIMAL_SEPARATOR,
): string =>
  roundLikeExcel(value, digits)
    .toFixed(digits)
    .replace(/\.?0+$/, '')
    .replace('.', decimalSeparator);

const getFieldDigits = (field: DraftInputField): number => {
  if (field === 'lateralOffset') {
    return OFFSET_DIGITS;
  }
  return WIDTH_DIGITS;
};

/**
 * Normalizes a field on blur: line counts become integers and decimals are
 * rounded and rewritten with the user's separator.
 *
 * @remarks
 * Unparseable input and negative values (except the lateral offset, which can
 * point either way) are left untouched, so their validation error stays on
 * screen until the user fixes them. Clamping a negative to zero would hide the
 * error and silently turn bad input into a plausible result.
 */
export const normalizeDraftField = (
  field: DraftInputField,
  value: string,
  decimalSeparator = DEFAULT_DECIMAL_SEPARATOR,
): string => {
  const parsed = parseDecimal(value);
  if (parsed === null) {
    return value;
  }

  if (parsed < 0 && field !== 'lateralOffset') {
    return value;
  }

  if (field === 'lineCount') {
    return String(Math.round(parsed));
  }

  return formatDecimal(parsed, getFieldDigits(field), decimalSeparator);
};

/**
 * Implement width = line spacing × line count. Computed independently of the
 * other inputs so it shows up as soon as both values are typed. From the
 * second pass on, the width comes from the previous pass
 * (`implementWidthOverride`).
 */
export const computeImplementWidth = (
  draft: PassToPassDraft,
): number | null => {
  if (Number.isFinite(draft.implementWidthOverride)) {
    return roundLikeExcel(Number(draft.implementWidthOverride), WIDTH_DIGITS);
  }

  const spacing = parseDecimal(draft.lineSpacing);
  const rows = parseDecimal(draft.lineCount);
  if (spacing === null || rows === null || spacing < 0 || rows < 0) {
    return null;
  }

  return roundLikeExcel(Math.max(0, Math.round(rows)) * spacing, WIDTH_DIGITS);
};

const getValidationErrors = (draft: PassToPassDraft): ValidationErrorCode[] => {
  const spacing = parseDecimal(draft.lineSpacing);
  const rows = parseDecimal(draft.lineCount);
  const offset = parseDecimal(draft.lateralOffset);
  const measured12 = parseDecimal(draft.measuredSpacing12);
  const measured23 = parseDecimal(draft.measuredSpacing23);
  const errors: ValidationErrorCode[] = [];

  if (spacing === null) {
    errors.push('lineSpacingRequired');
  } else if (spacing < 0) {
    errors.push('lineSpacingNegative');
  }

  if (rows === null) {
    errors.push('lineCountRequired');
  } else if (rows < 0) {
    errors.push('lineCountNegative');
  }

  // Zero spacing, zero lines or a line count that rounds to zero (e.g. 0,4)
  // give an implement that does not exist
  const implementWidth = computeImplementWidth(draft);
  if (!errors.length && implementWidth !== null && implementWidth <= 0) {
    errors.push('implementWidthNotPositive');
  }

  if (offset === null) {
    errors.push('lateralOffsetRequired');
  }

  if (measured12 === null || measured23 === null) {
    errors.push('measuredSpacingRequired');
  } else if (measured12 < 0 || measured23 < 0) {
    errors.push('measuredSpacingNegative');
  }

  return errors;
};

/**
 * Half of the gap between a measured spacing and the planter's line spacing.
 *
 * @remarks
 * A zero measurement is read as "the passes matched exactly", so it produces
 * no correction. This mirrors the source spreadsheet the operators calibrate
 * with and is kept on purpose, even though geometrically a perfect match is
 * `measured === reference` (which already yields zero) and 0 would mean the
 * outer rows overlapped by one full spacing.
 */
const getMeasuredDelta = (measured: number, reference: number): number => {
  if (measured > 0) {
    return (measured - reference) / 2;
  }
  return 0;
};

/** The offset correction points the opposite way when the turn is to the left. */
const getTurnFactor = (direction: TurnDirection): number => {
  if (direction === 'right') {
    return 1;
  }
  return -1;
};

export const calculatePassToPass = (
  draft: PassToPassDraft,
): PassToPassCalculation => {
  const errors = getValidationErrors(draft);
  const implementWidth = computeImplementWidth(draft);

  if (errors.length) {
    return {
      isValid: false,
      errors,
      implementWidth,
      correctedWidth: null,
      correctedOffset: null,
    };
  }

  const spacing = parseDecimal(draft.lineSpacing)!;
  const initialOffset = roundLikeExcel(
    parseDecimal(draft.lateralOffset)!,
    OFFSET_DIGITS,
  );
  const measured12 = roundLikeExcel(
    parseDecimal(draft.measuredSpacing12)!,
    WIDTH_DIGITS,
  );
  const measured23 = roundLikeExcel(
    parseDecimal(draft.measuredSpacing23)!,
    WIDTH_DIGITS,
  );
  const referenceSpacing = roundLikeExcel(spacing, WIDTH_DIGITS);
  const halfWidth = (implementWidth ?? 0) / 2;
  const leftCorrection =
    halfWidth - getMeasuredDelta(measured12, referenceSpacing);
  const rightCorrection =
    halfWidth - getMeasuredDelta(measured23, referenceSpacing);
  const correctedWidth = roundLikeExcel(
    leftCorrection + rightCorrection,
    WIDTH_DIGITS,
  );
  const correctedOffset = roundLikeExcel(
    initialOffset +
      ((leftCorrection - rightCorrection) / 2) *
        getTurnFactor(draft.turnDirection),
    OFFSET_DIGITS,
  );

  // Typically measurements typed in centimeters instead of meters; a
  // non-positive width must never be offered for the terminal or the next pass
  if (correctedWidth <= 0) {
    return {
      isValid: false,
      errors: ['correctedWidthNotPositive'],
      implementWidth,
      correctedWidth: null,
      correctedOffset: null,
    };
  }

  return {
    isValid: true,
    errors: [],
    implementWidth,
    correctedWidth,
    correctedOffset,
  };
};

export const canAdvanceToNextPass = (
  calculation: PassToPassCalculation,
): boolean =>
  calculation.isValid &&
  calculation.implementWidth !== null &&
  calculation.implementWidth > 0 &&
  calculation.correctedWidth !== null &&
  calculation.correctedOffset !== null;

/**
 * Closes the current pass and prepares the next one: the corrected values
 * become the inputs of the following pass and the field measurements are
 * cleared. Since the calculation does not depend on the pass number, this can
 * be repeated indefinitely. Returns null while the current pass is not valid.
 */
export const advanceToNextPass = (
  draft: PassToPassDraft,
  calculation: PassToPassCalculation,
  decimalSeparator = DEFAULT_DECIMAL_SEPARATOR,
): PassToPassDraft | null => {
  if (
    !canAdvanceToNextPass(calculation) ||
    calculation.implementWidth === null ||
    calculation.correctedWidth === null ||
    calculation.correctedOffset === null
  ) {
    return null;
  }

  const snapshot: PassSnapshot = {
    passNumber: draft.passNumber,
    implementWidth: calculation.implementWidth,
    lateralOffset: roundLikeExcel(
      parseDecimal(draft.lateralOffset) ?? 0,
      OFFSET_DIGITS,
    ),
    turnDirection: draft.turnDirection,
    measuredSpacing12: draft.measuredSpacing12,
    measuredSpacing23: draft.measuredSpacing23,
    correctedWidth: calculation.correctedWidth,
    correctedOffset: calculation.correctedOffset,
  };

  return {
    ...draft,
    passNumber: draft.passNumber + 1,
    implementWidthOverride: calculation.correctedWidth,
    lateralOffset: formatDecimal(
      calculation.correctedOffset,
      OFFSET_DIGITS,
      decimalSeparator,
    ),
    measuredSpacing12: '',
    measuredSpacing23: '',
    history: [...draft.history, snapshot],
  };
};

/**
 * Undoes the last pass, restoring its inputs and recorded measurements.
 * Going back to pass 1 derives the width from spacing × lines again, which
 * unlocks the initial values for editing.
 */
export const revertToPreviousPass = (
  draft: PassToPassDraft,
  decimalSeparator = DEFAULT_DECIMAL_SEPARATOR,
): PassToPassDraft => {
  const previous = draft.history[draft.history.length - 1];
  if (!previous) {
    return draft;
  }

  let implementWidthOverride: number | null = previous.implementWidth;
  if (previous.passNumber <= FIRST_PASS_NUMBER) {
    implementWidthOverride = null;
  }

  return {
    ...draft,
    passNumber: previous.passNumber,
    implementWidthOverride,
    lateralOffset: formatDecimal(
      previous.lateralOffset,
      OFFSET_DIGITS,
      decimalSeparator,
    ),
    turnDirection: previous.turnDirection,
    measuredSpacing12: previous.measuredSpacing12,
    measuredSpacing23: previous.measuredSpacing23,
    history: draft.history.slice(0, -1),
  };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const toText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  return '';
};

const toTurnDirection = (value: unknown): TurnDirection => {
  if (value === 'right') {
    return 'right';
  }
  return 'left';
};

const toSnapshot = (value: unknown): PassSnapshot | null => {
  if (!isPlainObject(value)) {
    return null;
  }

  const passNumber = toFiniteNumber(value.passNumber);
  const implementWidth = toFiniteNumber(value.implementWidth);
  const correctedWidth = toFiniteNumber(value.correctedWidth);
  const correctedOffset = toFiniteNumber(value.correctedOffset);

  if (
    passNumber === null ||
    implementWidth === null ||
    correctedWidth === null ||
    correctedOffset === null
  ) {
    return null;
  }

  return {
    passNumber: Math.max(FIRST_PASS_NUMBER, Math.round(passNumber)),
    implementWidth,
    lateralOffset: toFiniteNumber(value.lateralOffset) ?? 0,
    turnDirection: toTurnDirection(value.turnDirection),
    measuredSpacing12: toText(value.measuredSpacing12),
    measuredSpacing23: toText(value.measuredSpacing23),
    correctedWidth,
    correctedOffset,
  };
};

const toHistory = (value: unknown, passNumber: number): PassSnapshot[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  // A pass can only have as many history entries as passes before it
  return value
    .map(toSnapshot)
    .filter((entry): entry is PassSnapshot => entry !== null)
    .slice(0, passNumber - FIRST_PASS_NUMBER);
};

/**
 * Rebuilds a draft read from browser storage. Every field is validated
 * because the stored JSON may be corrupted, edited by hand or written by an
 * older version of the page; missing or invalid fields fall back to the
 * empty draft values. Returns null when the stored value is not an object.
 */
export const normalizeStoredDraft = (raw: unknown): PassToPassDraft | null => {
  if (!isPlainObject(raw)) {
    return null;
  }

  const passNumber = Math.max(
    FIRST_PASS_NUMBER,
    Math.round(toFiniteNumber(raw.passNumber) ?? FIRST_PASS_NUMBER),
  );

  return {
    passNumber,
    lineSpacing: toText(raw.lineSpacing),
    lineCount: toText(raw.lineCount),
    lateralOffset: toText(raw.lateralOffset),
    turnDirection: toTurnDirection(raw.turnDirection),
    measuredSpacing12: toText(raw.measuredSpacing12),
    measuredSpacing23: toText(raw.measuredSpacing23),
    implementWidthOverride: toFiniteNumber(raw.implementWidthOverride),
    history: toHistory(raw.history, passNumber),
  };
};
